import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import AddIcon from "@mui/icons-material/Add";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import {
  useAddPartnerMutation,
  useUpdatePartnerMutation,
} from "../../Slices/roomNateApis";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { LoadingButton } from "@mui/lab";
import { SketchPicker } from "react-color";
import PartnerLogoUpload from "./PartnerLogoUpload";
import { getPartnerLogoPreviewUrl } from "../../utils/partnerLogoUrl";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "auto",
  maxHeight: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function BasicModal({ editPartner, setEditPartner, packages }) {
  const formRef = React.useRef();
  const fieldRefs = React.useRef({});

  const [addPartner, resultStats] = useAddPartnerMutation();
  const [updatePartner, updateResultStats] = useUpdatePartnerMutation();

  const [open, setOpen] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const [showColorPicker, setShowColorPicker] = React.useState(false);

  const colorPickerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      )
        setShowColorPicker(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpen = React.useCallback(() => setOpen(true), []);
  const handleClose = React.useCallback(() => setOpen(false), []);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    domain: Yup.string().required("Domain is required"),
  });

  React.useEffect(() => {
    if (resultStats?.isLoading) {
      setLoad(true);
    }

    if (!resultStats?.isLoading) {
      setLoad(false);
    }

    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats, handleClose]);

  React.useEffect(() => {
    if (updateResultStats?.isLoading) {
      setLoad(true);
    }

    if (!updateResultStats?.isLoading) {
      setLoad(false);
    }

    if (updateResultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
      setEditPartner({});
    }

    if (updateResultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [updateResultStats, handleClose, setEditPartner]);

  const scrollToError = (errors) => {
    const firstErrorKey = Object.keys(errors)[0];

    if (firstErrorKey !== "variants") {
      if (fieldRefs.current[firstErrorKey]) {
        fieldRefs.current[firstErrorKey].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      const firstVariantIndex = Object.keys(errors.variants)[0];

      if (firstVariantIndex !== undefined) {
        const firstFieldKey = Object.keys(
          errors.variants[firstVariantIndex]
        )[0];
        const refKey = `variants.[${firstVariantIndex}].${firstFieldKey}`;

        if (fieldRefs.current[refKey]) {
          fieldRefs.current[refKey].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  };

  // Track previous open state to detect modal close transition
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    // Only clear editPartner when modal actually CLOSES (was open, now closed)
    if (prevOpenRef.current && !open && editPartner?._id) {
      setEditPartner({});
    }
    prevOpenRef.current = open;
  }, [open, editPartner?._id, setEditPartner]);

  React.useEffect(() => {
    // DEBUG: Log editPartner data when it changes (dev only)
    if (process.env.NODE_ENV !== 'production') {
      console.log('[AddPartnerModal] 🔍 DEBUG - editPartner:', editPartner);
      console.log('[AddPartnerModal] 🔍 DEBUG - editPartner._id:', editPartner?._id);
      console.log('[AddPartnerModal] 🔍 DEBUG - editPartner.title:', editPartner?.title);
    }
    if (editPartner?._id) handleOpen();
  }, [editPartner?._id, handleOpen, editPartner]);

  return (
    <>
      {successAlert && (
        <SuccessAlert title="Partner has been created successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      <Button onClick={handleOpen} variant="outlined" endIcon={<AddIcon />}>
        Add Partner
      </Button>

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box position="fixed" top={10} right={0}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              title: editPartner?.title || "",
              domain: editPartner?.domain || "",
              primaryColor: editPartner?.primaryColor || undefined,
              referralLabel: editPartner?.referralLabel || null,
              referralCode: editPartner?.referralCode || null,
              partnerPackage: editPartner?.partnerPackage || null,
              autoApproveUser: editPartner?.autoApproveUser || false,
              allowGuestUpload: editPartner?.allowGuestUpload || false,
              email: editPartner?.email || null,
              // `logo` is the partner's S3 key (or null to clear). Untouched
              // edits keep the original key so the PUT round-trips a no-op.
              logo: editPartner?.logo || null,
              // Track whether the logo was modified in this session. Without
              // this flag we can't tell "user kept current logo" from
              // "user staged a remove" once both end up as the same value.
              logoTouched: false,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              const {
                title,
                domain,
                primaryColor,
                referralLabel,
                referralCode,
                partnerPackage,
                autoApproveUser,
                allowGuestUpload,
                email,
                logo,
                logoTouched,
              } = values;

              const data = {
                title,
                domain,
                primaryColor,
                referralLabel,
                referralCode,
                partnerPackage,
                autoApproveUser: allowGuestUpload? allowGuestUpload:autoApproveUser,
                allowGuestUpload,
                email
              };

              // Only send `logo` if it was actually touched in this session.
              // On create, always include it so a fresh partner can be seeded
              // with a logo. On update, omitting an untouched field avoids
              // accidentally clobbering a logo set by another path.
              if (!editPartner?._id || logoTouched) {
                data.logo = logo ?? null;
              }

              if (editPartner?._id)
                updatePartner({ id: editPartner?._id, data });
              else addPartner(data);
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setFieldValue,
              validateForm,
            }) => (
              <Form noValidate autoComplete="off">
                <Typography
                  id="modal-modal-title"
                  fontWeight={"bold"}
                  fontSize={"20px"}
                  variant="h6"
                  mb={2}
                >
                  {editPartner?._id ? "Update" : "Add"} Partner
                </Typography>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Title"
                    variants="outlined"
                    name="title"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.title}
                    required
                    ref={(el) => (fieldRefs.current["title"] = el)}
                    helperText={
                      getIn(touched, "title") && getIn(errors, "title")
                        ? getIn(errors, "title")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "title") && getIn(errors, "title")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Domain"
                    variants="outlined"
                    name="domain"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.domain}
                    required
                    ref={(el) => (fieldRefs.current["domain"] = el)}
                    helperText={
                      getIn(touched, "domain") && getIn(errors, "domain")
                        ? getIn(errors, "domain")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "domain") && getIn(errors, "domain")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Email"
                    variants="outlined"
                    name="email"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    required
                    ref={(el) => (fieldRefs.current["email"] = el)}
                    helperText={
                      getIn(touched, "email") && getIn(errors, "email")
                        ? getIn(errors, "email")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "email") && getIn(errors, "email")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Referral Label"
                    variants="outlined"
                    name="referralLabel"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.referralLabel}
                    required
                    ref={(el) => (fieldRefs.current["referralLabel"] = el)}
                    helperText={
                      getIn(touched, "referralLabel") &&
                      getIn(errors, "referralLabel")
                        ? getIn(errors, "referralLabel")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "referralLabel") &&
                        getIn(errors, "referralLabel")
                    )}
                  />
                </Stack>


                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Referral Code"
                    variants="outlined"
                    name="referralCode"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.referralCode}
                    required
                    ref={(el) => (fieldRefs.current["referralCode"] = el)}
                    helperText={
                      getIn(touched, "referralCode") &&
                      getIn(errors, "referralCode")
                        ? getIn(errors, "referralCode")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "referralCode") &&
                        getIn(errors, "referralCode")
                    )}
                  />
                </Stack>



                {editPartner?._id && (
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={Boolean(
                      getIn(touched, "partnerPackage") &&
                        getIn(errors, "partnerPackage")
                    )}
                  >
                    <InputLabel id="partnerPackage-label">
                      Partner Package
                    </InputLabel>
                    <Select
                      labelId="partnerPackage-label"
                      id="partnerPackage"
                      name="partnerPackage"
                      value={values.partnerPackage || ""}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue(
                          "partnerPackage",
                          value === "" ? null : value
                        );
                      }}
                      onBlur={handleBlur}
                      label="Partner Package"
                      required
                    >
                      <MenuItem value="">
                        <em>None</em> 
                      </MenuItem>

                      {packages.map((pkg) => (
                        <MenuItem key={pkg._id} value={pkg._id}>
                          {pkg.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {getIn(touched, "partnerPackage") &&
                      getIn(errors, "partnerPackage") && (
                        <FormHelperText>
                          {getIn(errors, "partnerPackage")}
                        </FormHelperText>
                      )}
                  </FormControl>
                )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={values.allowGuestUpload? values.allowGuestUpload:values.autoApproveUser}
                  onChange={handleChange}
                  name="autoApproveUser"
                  disabled={values.allowGuestUpload}
                  color="primary"
                />
              }
              label="Auto Approve User"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={values.allowGuestUpload}
                  onChange={handleChange}
                  name="allowGuestUpload"
                  color="primary"
                />
              }
              label="Allow Guest Upload"
            />


                <PartnerLogoUpload
                  value={values.logo}
                  previewUrl={getPartnerLogoPreviewUrl(editPartner)}
                  onChange={(nextId) => {
                    setFieldValue("logo", nextId ?? null);
                    setFieldValue("logoTouched", true);
                  }}
                />

                <Stack mt={2} direction="row" alignItems="center">
                  <label htmlFor="primaryColor" style={{ marginRight: "10px" }}>
                    Primary Color:
                  </label>
                  <Box
                    onClick={() => setShowColorPicker(true)}
                    sx={{
                      width: "50px",
                      height: "50px",
                      backgroundColor: values.primaryColor,
                      border: "1px solid #ccc",
                      cursor: "pointer",
                    }}
                  />

                  {showColorPicker && (
                    <Box
                      ref={colorPickerRef}
                      sx={{
                        position: "absolute",
                        top: 20,
                        left: 230,
                        zIndex: 1,
                      }}
                    >
                      <SketchPicker
                        color={values.primaryColor}
                        onChange={(color) =>
                          setFieldValue("primaryColor", color.hex)
                        }
                      />
                    </Box>
                  )}
                </Stack>

                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                    onClick={() => {
                      validateForm().then((errors) => {
                        if (Object.keys(errors).length > 0) {
                          scrollToError(errors);
                        }
                      });
                    }}
                  >
                    {editPartner?._id ? "Update" : "Add"}
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
